import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { db, storage } from "../firebaseConfig";
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, limit } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getBlob } from "firebase/storage";
import Select from "react-select";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const U = (s = "") => (s || "").toString().trim().toUpperCase();
const normalizePlate = (s = "") => U(s).replace(/[^A-Z0-9]/g, "");
const formatPlateWithSpaces = (s = "") => {
  const raw = normalizePlate(s);
  const m = raw.match(/^(\d{2})([A-Z]{1,3})(\d{2,4})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]}` : raw;
};
const plateQueryValues = (s = "") =>
  Array.from(new Set([normalizePlate(s), formatPlateWithSpaces(s)].filter(Boolean)));

/* ---- Türkçe normalize eşleştirme ----
   "Bınek", "binek", "BİNEK", "BINEK" → hepsi aynı key'e düşer */
const normalizeTR = (s = "") =>
  (s || "").toString().trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i").replace(/İ/g, "i")
    .replace(/ğ/g, "g").replace(/Ğ/g, "g")
    .replace(/ü/g, "u").replace(/Ü/g, "u")
    .replace(/ş/g, "s").replace(/Ş/g, "s")
    .replace(/ö/g, "o").replace(/Ö/g, "o")
    .replace(/ç/g, "c").replace(/Ç/g, "c");

// İki araç cinsi normalize edilmiş olarak eşleşiyor mu?
const cinsMatch = (a, b) => normalizeTR(a) === normalizeTR(b);
const plateMatch = (a, b) => normalizePlate(a) === normalizePlate(b);

const docTypeAppliesToVehicle = (docType, arac) => {
  if (!docType || !arac) return false;
  const targetCins = Array.isArray(docType.targetCins) ? docType.targetCins : [];
  const targetPlates = Array.isArray(docType.targetPlates) ? docType.targetPlates : [];
  const cinsOk = targetCins.length === 0 || targetCins.some(c => cinsMatch(c, arac.aracCinsi));
  const plateOk = targetPlates.length === 0 || targetPlates.some(p => plateMatch(p, arac.plate));
  return cinsOk && plateOk && (targetCins.length > 0 || targetPlates.length > 0);
};

const getVehicleBom = (arac) => {
  const raw = arac?.bomUzunlugu ?? arac?.bomUzunluğu ?? arac?.bomLength ?? "";
  const n = Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const ruleMatchesVehicle = (rule, arac) => {
  const modelYear = parseInt(arac?.modelYear || arac?.model || arac?.year, 10);
  const currentYear = new Date().getFullYear();
  const aracYasi = modelYear ? currentYear - modelYear : null;
  const minAge = rule?.condition?.minAge ?? 0;
  const maxAge = rule?.condition?.maxAge ?? Infinity;
  const ageOk = aracYasi === null || (aracYasi >= minAge && aracYasi < maxAge);
  if (!ageOk) return false;

  const brand = rule?.condition?.brand || "";
  if (brand && normalizeTR(arac?.aracMarka) !== normalizeTR(brand)) return false;

  const modelName = rule?.condition?.modelName || "";
  if (modelName && normalizeTR(arac?.aracModel) !== normalizeTR(modelName)) return false;

  const bomMode = rule?.condition?.bomMode || "";
  if (bomMode) {
    const bom = getVehicleBom(arac);
    const min = rule?.condition?.bomMin;
    const max = rule?.condition?.bomMax;
    if (bom == null) return false;
    if (bomMode === "between") {
      if (min != null && bom < Number(min)) return false;
      if (max != null && bom > Number(max)) return false;
    } else if (min != null && bom < Number(min)) {
      return false;
    }
  }
  return true;
};

/* ---- Tarih yardımcıları ---- */
const todayDDMMYYYY = () => {
  const t = new Date();
  return `${String(t.getDate()).padStart(2,"0")}-${String(t.getMonth()+1).padStart(2,"0")}-${t.getFullYear()}`;
};
const isoToDDMMYYYY = (iso = "") => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
};
const ddmmyyyyToISO = (dmy = "") => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dmy || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : dmy;
};
const addMonthsDMY = (ddmmyyyy, months = 12) => {
  const [d, mo, y] = (ddmmyyyy || "").split("-").map(Number);
  if (!d || !mo || !y) return ddmmyyyy || "";
  const dt = new Date(y, mo - 1, d);
  const orig = dt.getDate();
  dt.setMonth(dt.getMonth() + months, 1);
  const last = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(orig, last));
  return `${String(dt.getDate()).padStart(2,"0")}-${String(dt.getMonth()+1).padStart(2,"0")}-${dt.getFullYear()}`;
};
const diffDays = (fromDMY, toDMY) => {
  const p = (s) => { const [d,m,y] = (s||"").split("-").map(Number); return new Date(y,(m||1)-1,d||1); };
  return Math.ceil((p(toDMY) - p(fromDMY)) / 86400000);
};
const fmt = (n) => n == null ? "—" : new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n);

/* ---- Kural helpers ---- */
const calcExpireDate = (docType, arac, docDateISO) => {
  const rules = docType?.rules;
  if (!rules || !rules.length) {
    const m = parseInt(docType?.validityMonth || docType?.validityMonths, 10) || 0;
    if (!m) return null;
    return addMonthsDMY(isoToDDMMYYYY(docDateISO), m);
  }
  let months = 0;
  for (const rule of rules) {
    if (ruleMatchesVehicle(rule, arac)) {
      months = parseInt(rule?.limits?.months, 10) || 0;
      break;
    }
  }
  if (!months) return null;
  return addMonthsDMY(isoToDDMMYYYY(docDateISO), months);
};

const calcValidityMonths = (docType, arac) => {
  const rules = docType?.rules;
  if (!rules || !rules.length) return parseInt(docType?.validityMonth || docType?.validityMonths, 10) || null;
  for (const rule of rules) {
    if (ruleMatchesVehicle(rule, arac)) return parseInt(rule?.limits?.months, 10) || null;
  }
  return null;
};

/**
 * Araç yaşına göre geçerli kuralı döndürür (limits + thresholds dahil)
 */
const getAppliedRule = (docType, arac) => {
  const rules = docType?.rules;
  if (!rules || !rules.length) return null;
  for (const rule of rules) {
    if (ruleMatchesVehicle(rule, arac)) return rule;
  }
  return null;
};

/* ---- fuel_transactions: belge tarihine göre önceki ve sonraki kaydı bul ---- */
async function fetchSurroundingTransactions(plate, docDateISO) {
  if (!plate || !docDateISO) return { prev: null, next: null };
  try {
    const docMs = new Date(docDateISO + "T12:00:00").getTime();
    const q = query(
      collection(db, "fuel_transactions"),
      where("vehiclePlate", "in", plateQueryValues(plate)),
      where("deleted", "==", false)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { prev: null, next: null };

    const parseNumber = (v) => { const n = Number(String(v || "").replace(",", ".")); return Number.isFinite(n) ? n : null; };
    const tsToMs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate().getTime();
      if (typeof v?.seconds === "number") return v.seconds * 1000;
      const d = new Date(v); return isNaN(d.getTime()) ? null : d.getTime();
    };

    const parsePosNum = (v) => { const n = parseNumber(v); return (n != null && n > 0) ? n : null; };
    const records = snap.docs.map(d => {
      const data = d.data();
      return {
        dms: tsToMs(data.date),
        km: parsePosNum(data.odometerKm),
        hour1: parsePosNum(data.hour1),
        hour2: parsePosNum(data.hour2),
      };
    }).filter(r => r.dms != null);

    // Önceki: belge tarihinden ÖNCE, en yakın
    const prevCandidates = records.filter(r => r.dms < docMs).sort((a, b) => b.dms - a.dms);
    // Sonraki: belge tarihinden SONRA, en yakın
    const nextCandidates = records.filter(r => r.dms > docMs).sort((a, b) => a.dms - b.dms);

    return {
      prev: prevCandidates[0] || null,
      next: nextCandidates[0] || null,
    };
  } catch (e) {
    console.error("fetchSurroundingTransactions hata:", e);
    return { prev: null, next: null };
  }
};

/* ---- fuel_transactions: bir araç için en son dolum kaydını bul ---- */
async function fetchLatestTransaction(plate) {
  if (!plate) return null;
  try {
    const q = query(
      collection(db, "fuel_transactions"),
      where("vehiclePlate", "in", plateQueryValues(plate)),
      where("deleted", "==", false)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const parsePosNum = (v) => { const n = Number(String(v || "").replace(",", ".")); return (Number.isFinite(n) && n > 0) ? n : null; };
    const tsToMs = (v) => {
      if (!v) return null;
      if (v?.toDate) return v.toDate().getTime();
      if (typeof v?.seconds === "number") return v.seconds * 1000;
      const d = new Date(v); return isNaN(d.getTime()) ? null : d.getTime();
    };

    const records = snap.docs.map(d => {
      const data = d.data();
      return { dms: tsToMs(data.date), km: parsePosNum(data.odometerKm), hour1: parsePosNum(data.hour1), hour2: parsePosNum(data.hour2) };
    }).filter(r => r.dms != null).sort((a, b) => b.dms - a.dms);

    return records[0] || null;
  } catch (e) {
    console.error("fetchLatestTransaction hata:", e);
    return null;
  }
}

const WARN_DAYS = 30;
const EMPTY_MULTI_FILTERS = { search: [], cins: [], tesis: [], docType: [] };

const toSelectOptions = (values) =>
  Array.from(new Set(values.filter(Boolean)))
    .sort((a, b) => String(a).localeCompare(String(b), "tr", { sensitivity: "base" }))
    .map((value) => ({ value, label: value }));

const matchesAnySelectedOrInput = (value, selectedTerms = [], activeInput = "") => {
  const haystack = U(value);
  const selected = selectedTerms.map((v) => U(v)).filter(Boolean);
  const input = U(activeInput);
  const selectedOk = selected.length === 0 || selected.some((term) => haystack.includes(term));
  const inputOk = !input || haystack.includes(input);
  return selectedOk && inputOk;
};

const withTimeout = (promise, ms, message = "Zaman aşımı") => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
};

const storagePathFromUrl = (fileUrl = "") => {
  const match = String(fileUrl).match(/\/o\/([^?]+)/);
  return match ? decodeURIComponent(match[1]) : "";
};

const VehicleDocUpload = () => {
  const { user, role } = useAuth();
  const isCreator = (role || "").toLocaleLowerCase("tr") === "oluşturucu";

  const [araclar, setAraclar] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [docs, setDocs] = useState([]);
  
  const [multiFilters, setMultiFilters] = useState(EMPTY_MULTI_FILTERS);
  const [selectedAracId, setSelectedAracId] = useState("");
  const [modal, setModal] = useState({ open: false, type: "", data: [] });
  const [loading, setLoading] = useState({ zip: false, upload: false });

  const [form, setForm] = useState({
    typeId: "",
    date: new Date().toISOString().split('T')[0],
    file: null,
    docKm: "",
    docHour1: "",
    docHour2: "",
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [validationWarning, setValidationWarning] = useState("");
  const [latestFuel, setLatestFuel] = useState(null);
  const [allLatestFuel, setAllLatestFuel] = useState({});
  const [fuelLoading, setFuelLoading] = useState(true);
  const [izinliTesisler, setIzinliTesisler] = useState(null); // null = henüz yüklenmedi

  useEffect(() => {
    (async () => {
      try {
        const [aSnap, tSnap, dSnap] = await Promise.all([
          getDocs(collection(db, "plates")),
          getDocs(collection(db, "aracDocTypes")),
          getDocs(collection(db, "aracDocs")),
        ]);

        const allPlates = aSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.active === true);
        setDocTypes(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setDocs(dSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Giriş yapan kullanıcının personeller kaydını bul → tesis + gorebilecegiFiloTesisler
        const currentEmail = user?.email || "";
        let izinli = null; // null = kısıtlama yok (oluşturucu)

        if (!isCreator && currentEmail) {
          try {
            const perSnap = await getDocs(
              query(collection(db, "personeller"), where("email", "==", currentEmail), limit(1))
            );
            if (!perSnap.empty) {
              const pData = perSnap.docs[0].data();
              const kendi = pData.tesis ? [String(pData.tesis).trim()] : [];
              const ekstra = Array.isArray(pData.gorebilecegiFiloTesisler)
                ? pData.gorebilecegiFiloTesisler.map(t => String(t).trim())
                : [];
              izinli = [...new Set([...kendi, ...ekstra])];
            } else {
              // personeller'de kaydı yok → hiçbir araç göremesin
              izinli = [];
            }
          } catch (e) { console.error("Personel tesis sorgusu hatası:", e); izinli = []; }
        }

        setIzinliTesisler(izinli);

        // Tesis filtresini uygula
        const filteredPlates = izinli === null
          ? allPlates
          : allPlates.filter(a => izinli.includes(String(a.tesis || "").trim()));
        setAraclar(filteredPlates);

        // Tüm araçlar için en son dolum kaydını hesapla
        const fSnap = await getDocs(query(collection(db, "fuel_transactions"), where("deleted", "==", false)));
        const parsePosNum = (v) => { const n = Number(String(v||"").replace(",",".")); return (Number.isFinite(n) && n > 0) ? n : null; };
        const tsToMs = (v) => {
          if (!v) return null;
          if (v?.toDate) return v.toDate().getTime();
          if (typeof v?.seconds === "number") return v.seconds * 1000;
          const d2 = new Date(v); return isNaN(d2.getTime()) ? null : d2.getTime();
        };
        const fuelMap = {};
        fSnap.docs.forEach(d => {
          const data = d.data();
          const plate = normalizePlate(data.vehiclePlate);
          if (!plate) return;
          const dms = tsToMs(data.date);
          if (dms == null) return;
          if (!fuelMap[plate] || dms > fuelMap[plate].dms) {
            fuelMap[plate] = { dms, km: parsePosNum(data.odometerKm), hour1: parsePosNum(data.hour1), hour2: parsePosNum(data.hour2) };
          }
        });
        setAllLatestFuel(fuelMap);
        setFuelLoading(false);
      } catch (err) { console.error(err); setFuelLoading(false); }
    })();
  }, [user, isCreator]);

  const selectedArac = useMemo(() => araclar.find(a => a.id === selectedAracId), [araclar, selectedAracId]);

  // Araç değişince en son dolum kaydını çek
  useEffect(() => {
    if (!selectedArac?.plate) { setLatestFuel(null); return; }
    fetchLatestTransaction(selectedArac.plate).then(setLatestFuel);
  }, [selectedArac?.plate]);

  // Seçili belge tipi ve uygulanacak kural
  const selectedDocType = useMemo(() => docTypes.find(t => t.id === form.typeId), [docTypes, form.typeId]);
  const appliedRule = useMemo(() => selectedDocType && selectedArac ? getAppliedRule(selectedDocType, selectedArac) : null, [selectedDocType, selectedArac]);

  // Kural parametreleri (ne sorgulanıyor?)
  const ruleParams = useMemo(() => {
    if (!appliedRule) return { months: 0, km: 0, workHours: 0, machineHours: 0 };
    return {
      months: parseInt(appliedRule.limits?.months, 10) || 0,
      km: parseInt(appliedRule.limits?.km, 10) || 0,
      workHours: parseInt(appliedRule.limits?.workHours, 10) || 0,
      machineHours: parseInt(appliedRule.limits?.machineHours, 10) || 0,
    };
  }, [appliedRule]);

  const numOrNull = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const getLiveDocRule = (d, arac) => {
    const type = docTypes.find(t => t.id === d.docTypeId);
    const liveRule = type && arac ? getAppliedRule(type, arac) : null;
    const limits = liveRule?.limits || {};
    const thresholds = liveRule?.thresholds || {};
    const fallback = d.appliedRule || {};
    return {
      type,
      months: parseInt(limits.months, 10) || parseInt(fallback.months, 10) || 0,
      km: parseInt(limits.km, 10) || parseInt(fallback.km, 10) || 0,
      workHours: parseInt(limits.workHours, 10) || parseInt(fallback.workHours, 10) || 0,
      machineHours: parseInt(limits.machineHours, 10) || parseInt(fallback.machineHours, 10) || 0,
      warnDays: parseInt(thresholds.days, 10) || parseInt(fallback.warnDays, 10) || WARN_DAYS,
      warnHours: parseInt(thresholds.hours, 10) || parseInt(fallback.warnHours, 10) || 0,
      warnMachineHours: parseInt(thresholds.machineHours, 10) || parseInt(fallback.warnMachineHours, 10) || 0,
      warnKm: parseInt(thresholds.km, 10) || parseInt(fallback.warnKm, 10) || 0,
    };
  };

  const getEffectiveDocValues = (d, arac) => {
    const ar = getLiveDocRule(d, arac);
    const docKm = numOrNull(d.docKm);
    const docHour1 = numOrNull(d.docHour1);
    const docHour2 = numOrNull(d.docHour2);
    const expireDate = ar.months > 0 && d.docDate
      ? addMonthsDMY(isoToDDMMYYYY(d.docDate), ar.months)
      : (d.expireDate || "");
    return {
      ar,
      expireDate,
      expireKm: ar.km > 0 && docKm != null ? docKm + ar.km : (d.expireKm ?? null),
      expireHour1: ar.workHours > 0 && docHour1 != null ? docHour1 + ar.workHours : (d.expireHour1 ?? null),
      expireHour2: ar.machineHours > 0 && docHour2 != null ? docHour2 + ar.machineHours : (d.expireHour2 ?? null),
    };
  };

  const buildStatusReasons = ({ ar, expireDate, expireKm, expireHour1, expireHour2, fuel, today }) => {
    const reasons = [];
    if (expireDate) {
      const rem = diffDays(today, expireDate);
      if (Number.isFinite(rem)) reasons.push({ label: "Tarih", value: rem, warnLimit: ar.warnDays || WARN_DAYS });
    }
    if (ar.km > 0 && expireKm != null && fuel.km != null) {
      reasons.push({ label: "KM", value: expireKm - fuel.km, warnLimit: ar.warnKm || 0 });
    }
    if (ar.workHours > 0 && expireHour1 != null && fuel.hour1 != null) {
      reasons.push({ label: "Araç Saati", value: expireHour1 - fuel.hour1, warnLimit: ar.warnHours || 0 });
    }
    if (ar.machineHours > 0 && expireHour2 != null && fuel.hour2 != null) {
      reasons.push({ label: "Makine Saati", value: expireHour2 - fuel.hour2, warnLimit: ar.warnMachineHours || 0 });
    }
    return reasons;
  };

  const summarizeReasons = (reasons, mode = "all") => {
    const selected = mode === "expired"
      ? reasons.filter(r => r.value < 0)
      : mode === "warn"
        ? reasons.filter(r => r.value >= 0 && r.value <= r.warnLimit)
        : reasons;
    return selected.map(r => `${r.label}: ${fmt(r.value)}`).join(" | ");
  };

  // Form değişince validasyon uyarısını sıfırla
  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setValidationWarning("");
  };

  const plateOptions = useMemo(() => toSelectOptions(araclar.map(a => a.plate)), [araclar]);
  const cinsOptions = useMemo(() => toSelectOptions(araclar.map(a => a.aracCinsi)), [araclar]);
  const tesisOptions = useMemo(() => toSelectOptions(araclar.map(a => a.tesis)), [araclar]);
  const docTypeOptions = useMemo(() => toSelectOptions(docTypes.map(dt => dt.docName)), [docTypes]);

  const multiSelectProps = {
    isMulti: true,
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
  };

  const selectValue = (options, key) => options.filter((o) => multiFilters[key].includes(o.value));
  const handleSelectFilter = (key) => (vals) => {
    setMultiFilters((prev) => ({ ...prev, [key]: (vals || []).map((v) => v.value) }));
  };

  const rightPanelData = useMemo(() => {
    if (!selectedArac) return { yuklenenler: [], eksikler: [] };
    const yuklenenler = docs.filter(d => d.aracId === selectedArac.id);
    const beklenenler = docTypes.filter(t => docTypeAppliesToVehicle(t, selectedArac));
    const eksikler = beklenenler.filter(t => !yuklenenler.some(d => d.docTypeId === t.id));
    return { yuklenenler, eksikler };
  }, [selectedArac, docs, docTypes]);

  const filteredAraclar = useMemo(() => {
    return araclar.filter(a => {
      const matchSearch = matchesAnySelectedOrInput(a.plate, multiFilters.search);
      const matchCins = matchesAnySelectedOrInput(a.aracCinsi, multiFilters.cins);
      const matchTesis = matchesAnySelectedOrInput(a.tesis, multiFilters.tesis);
      const docTypeTerms = multiFilters.docType.map((v) => U(v)).filter(Boolean);
      const matchDocType = docTypeTerms.length === 0 || docTypeTerms.some((term) =>
        docs.some(d => d.aracId === a.id && U(d.docTypeName).includes(term)) ||
        docTypes.some(t => U(t.docName).includes(term) && docTypeAppliesToVehicle(t, a))
      );
      return matchSearch && matchCins && matchTesis && matchDocType;
    });
  }, [araclar, multiFilters, docs, docTypes]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({ ...prev, file }));
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // ---- YÜKLEME FONKSİYONU ----
  const handleUpload = async () => {
    if (!selectedArac || !form.typeId || !form.file) return alert("Lütfen belge tipi ve dosya seçin.");

    const type = docTypes.find(t => t.id === form.typeId);
    const sameDocExists = docs.some(d =>
      normalizePlate(d.plate || "") === normalizePlate(selectedArac.plate || "") &&
      U(d.docTypeName || "") === U(type?.docName || "")
    );
    if (sameDocExists) {
      return alert(`${selectedArac.plate} plakalı araca "${type?.docName || "bu belge"}" daha önce yüklenmiş. Aynı belge adı aynı plakaya tekrar yüklenemez.`);
    }

    const rule = getAppliedRule(type, selectedArac);
    const limits = rule?.limits || {};
    const thresholds = rule?.thresholds || {};

    const docKmN = numOrNull(form.docKm);
    const docHour1N = numOrNull(form.docHour1);
    const docHour2N = numOrNull(form.docHour2);

    // ---- fuel_transactions validasyonu ----
    const needsKm = (parseInt(limits.km, 10) || 0) > 0;
    const needsHour = (parseInt(limits.workHours, 10) || 0) > 0;
    const needsMachineHour = (parseInt(limits.machineHours, 10) || 0) > 0;

    let warnings = [];

    if ((needsKm && docKmN != null) || (needsHour && docHour1N != null) || (needsMachineHour && docHour2N != null)) {
      const { prev, next } = await fetchSurroundingTransactions(selectedArac.plate, form.date);

      if (needsKm && docKmN != null) {
        if (prev && prev.km != null && docKmN < prev.km) {
          warnings.push(`⚠️ KM hatası: Girilen KM (${docKmN}) önceki dolum KM'sinden (${prev.km}) küçük.`);
        }
        if (next && next.km != null && docKmN > next.km) {
          warnings.push(`⚠️ KM hatası: Girilen KM (${docKmN}) sonraki dolum KM'sinden (${next.km}) büyük.`);
        }
        if (!next) {
          warnings.push(`ℹ️ Belge tarihinden sonra dolum kaydı bulunamadı. Yalnızca alt sınır kontrol edildi.`);
        }
      }

      if (needsHour && docHour1N != null) {
        if (prev && prev.hour1 != null && docHour1N < prev.hour1) {
          warnings.push(`⚠️ Araç saati hatası: Girilen saat (${docHour1N}) önceki dolum saatinden (${prev.hour1}) küçük.`);
        }
        if (next && next.hour1 != null && docHour1N > next.hour1) {
          warnings.push(`⚠️ Araç saati hatası: Girilen saat (${docHour1N}) sonraki dolum saatinden (${next.hour1}) büyük.`);
        }
      }

      if (needsMachineHour && docHour2N != null) {
        if (prev && prev.hour2 != null && docHour2N < prev.hour2) {
          warnings.push(`⚠️ Makine saati hatası: Girilen saat (${docHour2N}) önceki dolum makine saatinden (${prev.hour2}) küçük.`);
        }
        if (next && next.hour2 != null && docHour2N > next.hour2) {
          warnings.push(`⚠️ Makine saati hatası: Girilen saat (${docHour2N}) sonraki dolum makine saatinden (${next.hour2}) büyük.`);
        }
      }
    }

    // Hata varsa sor, uyarıysa kaydet ama bildir
    const hardErrors = warnings.filter(w => w.startsWith("⚠️ KM hatası") || w.startsWith("⚠️ Araç saati hatası") || w.startsWith("⚠️ Makine saati hatası"));
    if (hardErrors.length > 0) {
      const devam = window.confirm(hardErrors.join("\n") + "\n\nYine de kaydetmek istiyor musunuz?");
      if (!devam) return;
    } else if (warnings.length > 0) {
      setValidationWarning(warnings.join(" | "));
    }

    setLoading({ ...loading, upload: true });
    try {
      const storageRef = ref(storage, `arac_belgeleri/${selectedArac.plate}/${Date.now()}_${form.file.name}`);
      const uploadSnap = await uploadBytes(storageRef, form.file);
      const url = await getDownloadURL(uploadSnap.ref);

      const expireDate = calcExpireDate(type, selectedArac, form.date);

      // Bitiş KM ve Saat hesapla
      const expireKm = (needsKm && docKmN != null && limits.km) ? docKmN + parseInt(limits.km, 10) : null;
      const expireHour1 = (needsHour && docHour1N != null && limits.workHours) ? docHour1N + parseInt(limits.workHours, 10) : null;
      const expireHour2 = (needsMachineHour && docHour2N != null && limits.machineHours) ? docHour2N + parseInt(limits.machineHours, 10) : null;

      // appliedRule snapshot — kural sonradan değişse de eski belgeler doğru çalışsın
      const appliedRuleSnapshot = {
        months: parseInt(limits.months, 10) || 0,
        km: parseInt(limits.km, 10) || 0,
        workHours: parseInt(limits.workHours, 10) || 0,
        machineHours: parseInt(limits.machineHours, 10) || 0,
        warnDays: parseInt(thresholds.days, 10) || WARN_DAYS,
        warnHours: parseInt(thresholds.hours, 10) || 0,
        warnMachineHours: parseInt(thresholds.machineHours, 10) || 0,
        warnKm: parseInt(thresholds.km, 10) || 0,
      };

      const newDoc = {
        aracId: selectedArac.id,
        plate: selectedArac.plate,
        docTypeId: form.typeId,
        docTypeName: type.docName,
        docDate: form.date,
        expireDate: expireDate || "",
        docKm: docKmN,
        docHour1: docHour1N,
        docHour2: docHour2N,
        expireKm,
        expireHour1,
        expireHour2,
        appliedRule: appliedRuleSnapshot,
        fileUrl: url,
        fileName: form.file.name,
        createdAt: new Date().toISOString(),
      };

      const ref2 = await addDoc(collection(db, "aracDocs"), newDoc);
      setDocs(prev => [...prev, { id: ref2.id, ...newDoc }]);
      setForm({ typeId: "", date: new Date().toISOString().split('T')[0], file: null, docKm: "", docHour1: "", docHour2: "" });
      setPreviewUrl(null);
      setValidationWarning("");
      alert("Belge başarıyla yüklendi.");
    } catch (e) {
      console.error("UPLOAD HATASI:", e);
      alert(e.message);
    }
    setLoading({ ...loading, upload: false });
  };

  const handleDownloadZip = async () => {
    setLoading({ zip: true });
    try {
      const zip = new JSZip();
      const selectedPlateFilters = multiFilters.search.map(normalizePlate).filter(Boolean);
      const targetVehicles = selectedPlateFilters.length
        ? araclar.filter(a => selectedPlateFilters.some(p => normalizePlate(a.plate).includes(p)))
        : filteredAraclar;
      const targetIds = new Set(targetVehicles.map(a => a.id));
      const targetPlates = new Set(targetVehicles.map(a => normalizePlate(a.plate)));
      const plateById = new Map(araclar.map(a => [a.id, normalizePlate(a.plate)]));
      const targetDocs = docs.filter(d => {
        const docPlate = normalizePlate(d.plate || d.vehiclePlate || d.plaka) || plateById.get(d.aracId) || "";
        return (targetIds.has(d.aracId) || targetPlates.has(docPlate)) && (d.fileUrl || d.url || d.downloadURL || d.downloadUrl || d.fileURL);
      });
      if (!targetDocs.length) {
        alert("Seçili filtrelere ait indirilebilir belge bulunamadı.");
        return;
      }

      const loadBlob = async (fileUrl) => {
        const controller = new AbortController();
        const abortTimer = window.setTimeout(() => controller.abort(), 8000);
        try {
          const res = await fetch(fileUrl, { mode: "cors", signal: controller.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return await withTimeout(res.blob(), 8000, "Dosya okuma zaman aşımı");
        } finally {
          window.clearTimeout(abortTimer);
        }
      };

      const results = await Promise.all(targetDocs.map(async (d, i) => {
        const fileUrl = d.fileUrl || d.url || d.downloadURL || d.downloadUrl || d.fileURL;
        try {
          const blob = await loadBlob(fileUrl);
          return { ok: true, d, i, fileUrl, blob };
        } catch (fetchErr) {
          try {
            const storagePath = d.storagePath || d.fullPath || d.path || storagePathFromUrl(fileUrl);
            if (!storagePath) throw new Error("Storage path bulunamadı");
            const blob = await withTimeout(getBlob(ref(storage, storagePath)), 8000, "Storage dosya okuma zaman aşımı");
            return { ok: true, d, i, fileUrl, blob };
          } catch (storageErr) {
            console.error("Dosya indirme hatası:", d, fetchErr, storageErr);
            return { ok: false, d, i, fileUrl };
          }
        }
      }));

      const folder = zip.folder("belgeler");
      const successful = results.filter(r => r.ok);
      successful.forEach(({ d, i, fileUrl, blob }) => {
        const ext = (d.fileName || fileUrl || "").split("?")[0].split(".").pop() || "pdf";
        const docPlate = d.plate || d.vehiclePlate || d.plaka || "Plaka";
        folder.file(`${docPlate}_${d.docTypeName || "Belge"}_${i + 1}.${ext}`, blob);
      });

      if (!successful.length) {
        alert("Dosyalar ZIP'e eklenemedi. İlk hata ayrıntısı tarayıcı konsoluna yazıldı.");
        return;
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "Filtrelenmis_Arac_Belgeleri.zip");
      const failed = results.length - successful.length;
      if (failed) alert(`${successful.length} dosya ZIP'e eklendi, ${failed} dosya indirilemedi.`);
    } finally {
      setLoading({ zip: false });
    }
  };

  // ---- İSTATİSTİKLER ----
  const stats = useMemo(() => {
    const today = todayDDMMYYYY();
    let toplamList = [];
    let eksikList = [];
    let uyariList = [];
    let gecersizList = [];

    filteredAraclar.forEach(a => {
      const yuklenenler = docs.filter(d => d.aracId === a.id);
      const beklenenler = docTypes.filter(t => docTypeAppliesToVehicle(t, a));
      yuklenenler.forEach(d => {
        toplamList.push({ Plaka: a.plate, Tesis: a.tesis, Belge: d.docTypeName });
      });
      beklenenler.forEach(t => {
        const yuklu = yuklenenler.find(d => d.docTypeId === t.id);
        if (!yuklu) eksikList.push({ Plaka: a.plate, Tesis: a.tesis, Belge: t.docName });
      });
    const fuel = allLatestFuel[normalizePlate(a.plate)] || {};
      yuklenenler.forEach(d => {
        const values = getEffectiveDocValues(d, a);
        const reasons = buildStatusReasons({ ...values, fuel, today });
        const isExpired = reasons.some(r => r.value < 0);
        const isWarn = !isExpired && reasons.some(r => r.value >= 0 && r.value <= r.warnLimit);
        toplamList[toplamList.length - yuklenenler.length + yuklenenler.indexOf(d)] = {
          Plaka: a.plate,
          Tesis: a.tesis,
          Belge: d.docTypeName,
          Durum: summarizeReasons(reasons, "all")
        };
        if (isExpired) gecersizList.push({ Plaka: a.plate, Tesis: a.tesis, Belge: d.docTypeName, Durum: summarizeReasons(reasons, "expired") });
        else if (isWarn) uyariList.push({ Plaka: a.plate, Tesis: a.tesis, Belge: d.docTypeName, Durum: summarizeReasons(reasons, "warn") });
      });
    });
    return { toplam: toplamList, eksik: eksikList, uyari: uyariList, gecersiz: gecersizList };
  }, [filteredAraclar, docs, docTypes, allLatestFuel]);

  const getAracStats = (aracId, aracCinsi, plate) => {
    const today = todayDDMMYYYY();
    const yuklenenler = docs.filter(d => d.aracId === aracId);
    const arac = araclar.find(a => a.id === aracId) || { id: aracId, aracCinsi, plate };
    const beklenenler = docTypes.filter(t => docTypeAppliesToVehicle(t, arac));
    const eksik = beklenenler.filter(t => !yuklenenler.some(d => d.docTypeId === t.id)).length;
    const fuel = allLatestFuel[normalizePlate(plate)] || {};
    let gecersiz = 0, uyari = 0;
    yuklenenler.forEach(d => {
      const values = getEffectiveDocValues(d, arac);
      const reasons = buildStatusReasons({ ...values, fuel, today });
      const isExpired = reasons.some(r => r.value < 0);
      const isWarn = !isExpired && reasons.some(r => r.value >= 0 && r.value <= r.warnLimit);
      if (isExpired) gecersiz++; else if (isWarn) uyari++;
    });
    return { eksik, uyari, gecersiz, toplam: yuklenenler.length };
  };

  const exportToExcel = (data, fileName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Liste");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  // ---- Yüklü belge satırı için "kalan" hesabı (gerçek zamanlı) ----
  const calcDocStatus = (d) => {
    const today = todayDDMMYYYY();
    const values = getEffectiveDocValues(d, selectedArac);
    const ar = values.ar || {};

    const remDays = values.expireDate ? diffDays(today, values.expireDate) : null;
    const warnDays = ar.warnDays || WARN_DAYS;
    const isWarnDate = remDays != null && Number.isFinite(remDays) && remDays <= warnDays && remDays >= 0;
    const isExpiredDate = remDays != null && Number.isFinite(remDays) && remDays < 0;

    // Hangi parametre sorgulanıyor ama girilmemiş?
    const missingKm = ar.km > 0 && d.docKm == null;
    const missingHour = ar.workHours > 0 && d.docHour1 == null;
    const missingMachineHour = ar.machineHours > 0 && d.docHour2 == null;

    // Gerçek zamanlı KM durumu
    const currentKm = latestFuel?.km ?? null;
    const expireKm = values.expireKm ?? null;
    let remKm = null;
    let isExpiredKm = false;
    let isWarnKm = false;
    if (ar.km > 0 && expireKm != null && currentKm != null) {
      remKm = expireKm - currentKm;
      isExpiredKm = remKm < 0;
      isWarnKm = !isExpiredKm && remKm <= (ar.warnKm > 0 ? ar.warnKm : ar.km * 0.1);
    }

    // Gerçek zamanlı Saat durumu
    const currentHour = latestFuel?.hour1 ?? null;
    const expireHour1 = values.expireHour1 ?? null;
    let remHour = null;
    let isExpiredHour = false;
    let isWarnHour = false;
    if (ar.workHours > 0 && expireHour1 != null && currentHour != null) {
      remHour = expireHour1 - currentHour;
      isExpiredHour = remHour < 0;
      isWarnHour = !isExpiredHour && remHour <= (ar.warnHours > 0 ? ar.warnHours : ar.workHours * 0.1);
    }

    // Herhangi biri dolmuş veya uyarıda mı?
    const currentMachineHour = latestFuel?.hour2 ?? null;
    const expireHour2 = values.expireHour2 ?? null;
    let remMachineHour = null;
    let isExpiredMachineHour = false;
    let isWarnMachineHour = false;
    if (ar.machineHours > 0 && expireHour2 != null && currentMachineHour != null) {
      remMachineHour = expireHour2 - currentMachineHour;
      isExpiredMachineHour = remMachineHour < 0;
      isWarnMachineHour = !isExpiredMachineHour && remMachineHour <= (ar.warnMachineHours > 0 ? ar.warnMachineHours : ar.machineHours * 0.1);
    }

    const isAnyExpired = isExpiredDate || isExpiredKm || isExpiredHour || isExpiredMachineHour;
    const isAnyWarn = !isAnyExpired && (isWarnDate || isWarnKm || isWarnHour || isWarnMachineHour);

    return {
      remDays, remKm, remHour, remMachineHour,
      missingKm, missingHour, missingMachineHour,
      isWarnDate, isWarnKm, isWarnHour, isWarnMachineHour,
      isExpiredDate, isExpiredKm, isExpiredHour, isExpiredMachineHour,
      isAnyExpired, isAnyWarn,
      currentKm, currentHour, currentMachineHour,
      expireDate: values.expireDate,
      expireKm,
      expireHour1,
      expireHour2,
      ar,
    };
  };

  return (
    <div style={{ padding: '25px', backgroundColor: '#f1f3f4', minHeight: '100vh', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      
      <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: '0 20px 0 0', fontSize: '24px', fontWeight: 'bold' }}>Belge Yükleme</h2>
        
        <div style={sTopBubble} onClick={() => !fuelLoading && setModal({ open: true, type: "Toplam", data: stats.toplam })}>
          📄 Toplam Evrak: {stats.toplam.length}
        </div>
        <div style={{ ...sTopBubble, color: '#d32f2f', border: '1px solid #ffcdd2' }}
             onClick={() => !fuelLoading && setModal({ open: true, type: "Eksik", data: stats.eksik })}>
          ⚠️ Eksik Evrak: {stats.eksik.length}
        </div>
        <div style={{ ...sTopBubble, color: '#b91c1c', border: '1px solid #fecaca', background: stats.gecersiz.length > 0 ? '#fff5f5' : '#fff' }}
             onClick={() => !fuelLoading && setModal({ open: true, type: "Geçersiz", data: stats.gecersiz })}>
          {fuelLoading
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>🔴 Geçersiz <span style={sSpinner} /></span>
            : `🔴 Geçersiz Evrak: ${stats.gecersiz.length}`}
        </div>
        <div style={{ ...sTopBubble, color: '#f57c00', border: '1px solid #ffe0b2' }}
             onClick={() => !fuelLoading && setModal({ open: true, type: "Uyarı", data: stats.uyari })}>
          {fuelLoading
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>⏰ Uyarı <span style={sSpinner} /></span>
            : `⏰ Uyarı Süresinde: ${stats.uyari.length}`}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '25px' }}>
        
        {/* SOL PANEL */}
        <div style={{ background: '#fff', borderRadius: '15px', padding: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            <Select
              {...multiSelectProps}
              options={plateOptions}
              value={selectValue(plateOptions, "search")}
              onChange={handleSelectFilter("search")}
              placeholder="Plaka Ara..."
            />
            <Select
              {...multiSelectProps}
              options={cinsOptions}
              value={selectValue(cinsOptions, "cins")}
              onChange={handleSelectFilter("cins")}
              placeholder="Araç Cinsi..."
            />
            <Select
              {...multiSelectProps}
              options={tesisOptions}
              value={selectValue(tesisOptions, "tesis")}
              onChange={handleSelectFilter("tesis")}
              placeholder="Tesis Ara..."
            />
            <Select
              {...multiSelectProps}
              options={docTypeOptions}
              value={selectValue(docTypeOptions, "docType")}
              onChange={handleSelectFilter("docType")}
              placeholder="Belge Tipi..."
            />
            <button onClick={handleDownloadZip} disabled={loading.zip} style={{ ...sBtnZip, opacity: loading.zip ? 0.7 : 1 }}>
              {loading.zip ? "Dosyalar Hazırlanıyor..." : "📁 Filtredeki Belgeleri ZIP İndir"}
            </button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1, paddingRight: '5px' }}>
            {filteredAraclar.map(a => {
              const aStats = getAracStats(a.id, a.aracCinsi, a.plate);
              const isSelected = selectedAracId === a.id;
              return (
                <div key={a.id} onClick={() => setSelectedAracId(a.id)} style={{
                  padding: '16px', borderRadius: '12px', marginBottom: '12px', cursor: 'pointer',
                  background: isSelected ? '#eff4ff' : '#fff', transition: 'all 0.2s ease',
                  border: isSelected ? '1px solid #3f51b5' : '1px solid #eee',
                  borderLeft: isSelected ? '6px solid #3f51b5' : '6px solid transparent',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div style={{ flex: 1, marginRight: '10px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#111' }}>{U(a.plate)}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{U(a.aracCinsi)} — {U(a.tesis)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <div style={{ ...sBubble, background: aStats.eksik > 0 ? '#ffebee' : '#f8f9fa', borderColor: aStats.eksik > 0 ? '#ffcdd2' : '#eee' }}>
                      <span style={sLabel}>Eksik</span>
                      <span style={{ ...sValue, color: aStats.eksik > 0 ? '#c62828' : '#333' }}>{aStats.eksik}</span>
                    </div>
                    <div style={{ ...sBubble, background: aStats.gecersiz > 0 ? '#fff5f5' : '#f8f9fa', borderColor: aStats.gecersiz > 0 ? '#fecaca' : '#eee' }}>
                      <span style={sLabel}>Geçrsz</span>
                      <span style={{ ...sValue, color: aStats.gecersiz > 0 ? '#b91c1c' : '#333' }}>{aStats.gecersiz}</span>
                    </div>
                    <div style={{ ...sBubble, background: aStats.uyari > 0 ? '#fffbeb' : '#f8f9fa', borderColor: aStats.uyari > 0 ? '#fde68a' : '#eee' }}>
                      <span style={sLabel}>Uyarı</span>
                      <span style={{ ...sValue, color: aStats.uyari > 0 ? '#b45309' : '#333' }}>{aStats.uyari}</span>
                    </div>
                    <div style={sBubble}><span style={sLabel}>Top</span><span style={sValue}>{aStats.toplam}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SAĞ PANEL */}
        <div style={{ background: '#fff', borderRadius: '15px', padding: '30px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflowY: 'auto', height: 'calc(100vh - 180px)' }}>
          {selectedArac ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#1a237e' }}>{U(selectedArac.plate)} — {U(selectedArac.aracCinsi)}</h3>
                <button style={sBtnZip} onClick={handleDownloadZip}>📥 Tüm Evraklarını İndir (ZIP)</button>
              </div>

              {/* EKSİK BELGELER */}
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ color: '#d32f2f', margin: '0 0 10px 0', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Eksik Belgeler</h4>
                <table width="100%" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                      <th style={sPth}>Belge Adı</th>
                      <th style={sPth}>Zorunlu</th>
                      <th style={sPth}>Geçerlilik (Ay)</th>
                      <th style={sPth}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rightPanelData.eksikler.map(e => {
                      const vm = calcValidityMonths(e, selectedArac);
                      return (
                        <tr key={e.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={sPtd}>{e.docName}</td>
                          <td style={sPtd}>Evet</td>
                          <td style={sPtd}>{vm !== null ? vm : "—"}</td>
                          <td style={sPtd}>
                            <button style={sBtnMiniUpload} onClick={() => setForm(prev => ({...prev, typeId: e.id}))}>Yükle</button>
                          </td>
                        </tr>
                      );
                    })}
                    {!rightPanelData.eksikler.length && (
                      <tr><td colSpan={4} style={{ padding: 10, color: '#6b7280', fontSize: 13 }}>Eksik belge yok ✅</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* YÜKLÜ BELGELER */}
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ color: '#2e7d32', margin: '0 0 10px 0', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Yüklü Belgeler</h4>
                <table width="100%" style={{ borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#f8f9fa', textAlign: 'left' }}>
                    <tr>
                      <th style={sPth}>Belge</th>
                      <th style={sPth}>Belge Tarihi</th>
                      <th style={sPth}>Bitiş Tarihi</th>
                      <th style={sPth}>Kalan Gün</th>
                      <th style={sPth}>Belge KM</th>
                      <th style={sPth}>Bitiş KM</th>
                      <th style={sPth}>Belge Saati</th>
                      <th style={sPth}>Bitiş Saati</th>
                      <th style={sPth}>Mak. Saati</th>
                      <th style={sPth}>Bitiş Mak.</th>
                      <th style={sPth}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rightPanelData.yuklenenler.map(d => {
                      const st = calcDocStatus(d);
                      const ar = st.ar || {};

                      // Satır rengi: kırmızı = dolmuş, turuncu = uyarı
                      const rowBg = st.isAnyExpired ? '#fff5f5' : st.isAnyWarn ? '#fffbeb' : undefined;
                      const expiredColor = '#b91c1c';
                      const warnColor = '#b45309';

                      const tdDate = { ...sPtd,
                        color: st.isExpiredDate ? expiredColor : st.isWarnDate ? warnColor : undefined,
                        fontWeight: (st.isExpiredDate || st.isWarnDate) ? 'bold' : undefined
                      };
                      const tdKm = { ...sPtd,
                        color: st.isExpiredKm ? expiredColor : st.isWarnKm ? warnColor : undefined,
                        fontWeight: (st.isExpiredKm || st.isWarnKm) ? 'bold' : undefined
                      };
                      const tdHour = { ...sPtd,
                        color: st.isExpiredHour ? expiredColor : st.isWarnHour ? warnColor : undefined,
                        fontWeight: (st.isExpiredHour || st.isWarnHour) ? 'bold' : undefined
                      };
                      const tdMachineHour = { ...sPtd,
                        color: st.isExpiredMachineHour ? expiredColor : st.isWarnMachineHour ? warnColor : undefined,
                        fontWeight: (st.isExpiredMachineHour || st.isWarnMachineHour) ? 'bold' : undefined
                      };

                      return (
                        <tr key={d.id} style={{ borderBottom: '1px solid #eee', background: rowBg }}>
                          <td style={sPtd}>
                            <b>{d.docTypeName}</b>
                            {st.isAnyExpired && <span style={{ marginLeft: 6, fontSize: 11, color: expiredColor }}>● GEÇERSİZ</span>}
                            {st.isAnyWarn && <span style={{ marginLeft: 6, fontSize: 11, color: warnColor }}>● UYARI</span>}
                          </td>
                          <td style={sPtd}>{d.docDate ? isoToDDMMYYYY(d.docDate) : "—"}</td>

                          {/* Bitiş Tarihi */}
                          <td style={tdDate}>{st.expireDate || "—"}</td>

                          {/* Kalan Gün */}
                          <td style={tdDate}>
                            {st.remDays == null ? "—"
                              : st.isExpiredDate ? `${Math.abs(st.remDays)} gün önce doldu`
                              : `${st.remDays} gün`}
                          </td>

                          {/* Belge KM */}
                          <td style={sPtd}>
                            {ar.km > 0
                              ? (st.missingKm ? <span style={{ color: '#f57c00', fontSize: 11 }}>⚠️ Girilmemiş</span> : fmt(d.docKm))
                              : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                          </td>

                          {/* Bitiş KM */}
                          <td style={tdKm}>
                            {ar.km > 0
                              ? (st.missingKm
                                  ? <span style={{ color: '#f57c00', fontSize: 11 }}>⚠️ Girilmemiş</span>
                                  : st.expireKm != null
                                    ? <>
                                        {fmt(st.expireKm)} KM
                                        {st.currentKm != null && (
                                          <div style={{ fontSize: 10, marginTop: 1 }}>
                                            {st.isExpiredKm
                                              ? `🔴 ${fmt(Math.abs(st.remKm))} KM aşıldı`
                                              : `Kalan: ${fmt(st.remKm)} KM`}
                                          </div>
                                        )}
                                      </>
                                    : "—")
                              : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                          </td>

                          {/* Belge Saati */}
                          <td style={sPtd}>
                            {ar.workHours > 0
                              ? (st.missingHour ? <span style={{ color: '#f57c00', fontSize: 11 }}>⚠️ Girilmemiş</span> : fmt(d.docHour1))
                              : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                          </td>

                          {/* Bitiş Saati */}
                          <td style={tdHour}>
                            {ar.workHours > 0
                              ? (st.missingHour
                                  ? <span style={{ color: '#f57c00', fontSize: 11 }}>⚠️ Girilmemiş</span>
                                  : st.expireHour1 != null
                                    ? <>
                                        {fmt(st.expireHour1)} saat
                                        {st.currentHour != null && (
                                          <div style={{ fontSize: 10, marginTop: 1 }}>
                                            {st.isExpiredHour
                                              ? `🔴 ${fmt(Math.abs(st.remHour))} saat aşıldı`
                                              : `Kalan: ${fmt(st.remHour)} saat`}
                                          </div>
                                        )}
                                      </>
                                    : "—")
                              : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                          </td>

                          <td style={sPtd}>
                            {ar.machineHours > 0
                              ? (st.missingMachineHour ? <span style={{ color: '#f57c00', fontSize: 11 }}>Girilmemiş</span> : fmt(d.docHour2))
                              : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                          </td>

                          <td style={tdMachineHour}>
                            {ar.machineHours > 0
                              ? (st.missingMachineHour
                                  ? <span style={{ color: '#f57c00', fontSize: 11 }}>Girilmemiş</span>
                                  : st.expireHour2 != null
                                    ? <>
                                        {fmt(st.expireHour2)} saat
                                        {st.currentMachineHour != null && (
                                          <div style={{ fontSize: 10, marginTop: 1 }}>
                                            {st.isExpiredMachineHour
                                              ? `${fmt(Math.abs(st.remMachineHour))} saat aşıldı`
                                              : `Kalan: ${fmt(st.remMachineHour)} saat`}
                                          </div>
                                        )}
                                      </>
                                    : "—")
                              : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                          </td>

                          <td style={sPtd}>
                            <a href={d.fileUrl} target="_blank" rel="noreferrer" style={sPbtn}>Görüntüle</a>
                            <button onClick={() => deleteDoc(doc(db, "aracDocs", d.id)).then(() => setDocs(prev => prev.filter(x => x.id !== d.id)))}
                              style={{ ...sPbtn, background: '#d32f2f' }}>Sil</button>
                          </td>
                        </tr>
                      );
                    })}
                    {!rightPanelData.yuklenenler.length && (
                      <tr><td colSpan={11} style={{ padding: 10, color: '#6b7280', fontSize: 13 }}>Henüz belge yüklenmemiş.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* BELGE YÜKLEME FORMU */}
              <div style={{ borderTop: '2px solid #eee', paddingTop: '20px', background: '#fcfcfc', padding: '20px', borderRadius: '10px' }}>
                <h4 style={{ margin: '0 0 15px 0' }}>Belge Yükle</h4>

                {/* Satır 1: Belge Tipi, Belge Tarihi, Dosya, Yükle */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '15px', alignItems: 'flex-end', marginBottom: 12 }}>
                  <div>
                    <label style={sPlab}>Belge Tipi</label>
                    <select style={sPinp} value={form.typeId} onChange={e => handleFormChange("typeId", e.target.value)}>
                      <option value="">Seçiniz...</option>
                      {docTypes.filter(t => docTypeAppliesToVehicle(t, selectedArac)).map(t => (
                        <option key={t.id} value={t.id}>{t.docName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={sPlab}>Belge Tarihi</label>
                    <input type="date" style={sPinp} value={form.date} onChange={e => handleFormChange("date", e.target.value)} />
                  </div>
                  <div>
                    <label style={sPlab}>Dosya Seç (PDF/JPG/JPEG)</label>
                    <input type="file" accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg" onChange={handleFileChange} />
                  </div>
                  <button onClick={handleUpload} disabled={loading.upload} style={sPbtnUp}>
                    {loading.upload ? "..." : "Yükle"}
                  </button>
                </div>

                {/* Satır 2: KM ve Saat — sadece kural gerektiriyorsa göster */}
                {form.typeId && (ruleParams.km > 0 || ruleParams.workHours > 0 || ruleParams.machineHours > 0) && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: 12, padding: '12px', background: '#f0f4ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                    <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#3730a3', fontWeight: 600, marginBottom: 4 }}>
                      📏 Bu belge tipi için ek parametre gerekli:
                    </div>

                    {ruleParams.km > 0 && (
                      <div>
                        <label style={sPlab}>Belge KM <span style={{ color: '#c53030' }}>*</span></label>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={sPinp}
                          placeholder={`Mevcut KM (bitiş: +${fmt(ruleParams.km)})`}
                          value={form.docKm}
                          onChange={e => handleFormChange("docKm", e.target.value)}
                        />
                        {form.docKm && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Bitiş KM: {fmt(Number(form.docKm) + ruleParams.km)}</div>}
                      </div>
                    )}

                    {ruleParams.workHours > 0 && (
                      <>
                        <div>
                          <label style={sPlab}>Araç Saati <span style={{ color: '#c53030' }}>*</span></label>
                          <input
                            type="text"
                            inputMode="decimal"
                            style={sPinp}
                            placeholder={`Mevcut saat (bitiş: +${fmt(ruleParams.workHours)})`}
                            value={form.docHour1}
                            onChange={e => handleFormChange("docHour1", e.target.value)}
                          />
                          {form.docHour1 && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Bitiş Saati: {fmt(Number(form.docHour1) + ruleParams.workHours)}</div>}
                        </div>
                        {/* Makine saati — sadece cins POMPA ise */}
                      </>
                    )}

                    {ruleParams.machineHours > 0 && (
                      <div>
                        <label style={sPlab}>Makine Saati <span style={{ color: '#c53030' }}>*</span></label>
                        <input
                          type="text"
                          inputMode="decimal"
                          style={sPinp}
                          placeholder={`Makine saati (bitiş: +${fmt(ruleParams.machineHours)})`}
                          value={form.docHour2}
                          onChange={e => handleFormChange("docHour2", e.target.value)}
                        />
                        {form.docHour2 && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Bitiş Mak. Saati: {fmt(Number(form.docHour2) + ruleParams.machineHours)}</div>}
                      </div>
                    )}
                  </div>
                )}

                {/* Validasyon uyarısı */}
                {validationWarning && (
                  <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, color: '#92400e', fontSize: 13, marginBottom: 12 }}>
                    {validationWarning}
                  </div>
                )}

                {/* PDF önizleme */}
                {previewUrl && (
                  <div style={{ marginTop: '10px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                    <div style={{ background: '#eee', padding: '5px 15px', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Dosya Ön İzleme</span>
                      <button onClick={() => { setPreviewUrl(null); setForm(prev => ({...prev, file: null})); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'red' }}>Kapat</button>
                    </div>
                    <iframe src={previewUrl} width="100%" height="400px" title="Belge Ön İzleme" />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', marginTop: '100px', color: '#999', fontSize: '18px' }}>Araç seçiniz.</div>
          )}
        </div>
      </div>

      {/* MODAL */}
      {modal.open && (
        <div style={sOverlay}>
          <div style={sModalContent}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>{modal.type} Evrak Listesi ({modal.data.length})</h3>
              <button onClick={() => setModal({ ...modal, open: false })}>Kapat</button>
            </div>
            {modal.data.length > 0 && (
              <button style={sBtnExcel} onClick={() => exportToExcel(modal.data, `${modal.type}_Listesi`)}>Excel İndir</button>
            )}
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '15px' }}>
              <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr><th style={sTh}>Plaka</th><th style={sTh}>Tesis</th><th style={sTh}>Belge</th><th style={sTh}>Durum</th></tr>
                </thead>
                <tbody>
                  {modal.data.map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={sTd}>{item.Plaka}</td><td style={sTd}>{item.Tesis}</td><td style={sTd}>{item.Belge}</td><td style={sTd}>{item.Durum || "—"}</td>
                    </tr>
                  ))}
                  {!modal.data.length && <tr><td colSpan={4} style={{ padding: 10, color: '#6b7280' }}>Kayıt yok</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Stiller (orijinalden birebir alındı) ---- */
const sBubble = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '50%', background: '#f8f9fa', border: '1px solid #eee', flexShrink: 0 };
const sLabel = { fontSize: '9px', color: '#888', fontWeight: '600' };
const sValue = { fontSize: '13px', fontWeight: 'bold' };
const sTopBubble = { padding: '10px 20px', background: '#fff', borderRadius: '25px', fontSize: '14px', border: '1px solid #e0e0e0', cursor: 'pointer', fontWeight: '600', display: 'flex', gap: '8px', alignItems: 'center' };
const sOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const sModalContent = { background: '#fff', padding: '30px', borderRadius: '20px', width: '600px' };
const sBtnExcel = { padding: '10px 20px', background: '#1b5e20', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' };
const sBtnZip = { padding: '12px', background: '#3f51b5', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' };
const sTh = { padding: '10px', textAlign: 'left' };
const sTd = { padding: '10px' };
const sBtnMiniUpload = { padding: '4px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' };
const sPth = { padding: '10px', color: '#666', fontSize: '12px', borderBottom: '1px solid #eee' };
const sPtd = { padding: '10px', fontSize: '13px' };
const sPbtn = { padding: '4px 8px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', textDecoration: 'none', marginRight: '5px', display: 'inline-block' };
const sPlab = { display: 'block', fontSize: '11px', color: '#666', marginBottom: '4px' };
const sPinp = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box' };
const sPbtnUp = { padding: '10px 20px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' };

// Spinner — CSS animasyonu inline olarak inject ediyoruz
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `@keyframes _spin { to { transform: rotate(360deg); } }`;
if (!document.head.querySelector('style[data-vdu-spinner]')) {
  spinnerStyle.setAttribute('data-vdu-spinner', '1');
  document.head.appendChild(spinnerStyle);
}
const sSpinner = {
  display: 'inline-block', width: 14, height: 14,
  border: '2px solid #ccc', borderTopColor: '#666',
  borderRadius: '50%', animation: '_spin 0.7s linear infinite', flexShrink: 0
};

export default VehicleDocUpload;
